import { debounce } from 'lodash';
import api from '@js/api';
import ErrorHandler from '@js/ErrorHandler';
import {
	applyPayCartHttpErrors,
	finishPayCartResponse,
	restorePaymentFormIfAvailable,
} from '@js/checkout-pay-errors';
import {
	getUserData,
	checkEmailExists,
	validatePostalCode,
	getUserAddresses,
	getCartData,
	updateCartShippingAddress,
	updateCartBillingAddress,
	updateCartShippingMethod,
	syncCartAddressesForPayment,
	determineGateway,
	addOfferToCart,
	removeOfferFromCart,
	USA_CANADA_COUNTRY_CODES,
} from '@js/service/checkoutService';

export default {
	async initialize({
		commit,
		state
	}, refreshUserData = false) {
		let user = null;
		if (refreshUserData) {
			user = await getUserData();
		} else {
			user = window.user;
		}

		if (user) {
			commit('setUser', user);

			const {
				billingAddress,
				shippingAddress
			} =
				await getUserAddresses();

			if (billingAddress.id) {
				commit('setBillingAddress', { ...billingAddress });
			}

			if (shippingAddress.id) {
				commit('setShippingAddress', {
					...shippingAddress,
					country: USA_CANADA_COUNTRY_CODES.includes(shippingAddress.country) ? shippingAddress.country : undefined,
				});
			}

			commit(
				'setShippingDifferentFromBilling',
				billingAddress.id &&
				shippingAddress.id &&
				billingAddress.id !== shippingAddress.id
			);
		}

		const cartData = await getCartData(state.params, state.query);

		if (!cartData || !cartData.order_offers.length) {
			window.location.href = '/cart';
			return;
		}

		commit('setCart', cartData);

		try {
			const {
				billingComponent,
				billingGateway
			} = determineGateway();
			commit('setBillingComponent', billingComponent);
			commit('setBillingGateway', billingGateway);
		} catch (error) {
			ErrorHandler.reportError(error.message);
		}

		commit('setInitialized', true);
	},

	async addOfferToCart({ commit }, offerId) {
		commit('setModifyingCart', true);
		const { data } = await addOfferToCart(offerId);
		commit('setCart', data.data);
		commit('setModifyingCart', false);
	},

	async removeOfferFromCart({
		commit,
		getters
	}, offerId) {
		commit('setModifyingCart', true);

		const cartItem = getters.itemForOffer(offerId);

		if (cartItem) {
			const { data } = await removeOfferFromCart(cartItem.id);
			commit('setCart', data);
		}

		commit('setModifyingCart', false);
	},

	updateShippingMethod: debounce(async ({ commit }, shippingMethodId) => {
		commit('setModifyingCart', true);
		commit('setCart', await updateCartShippingMethod(shippingMethodId));
		commit('setModifyingCart', false);
	}, 500),

	refreshTaxAndShipping: debounce(
		async ({
			getters,
			commit
		}) => {
			if (getters.taxAndShippingAddressHasErrors) return;

			if (getters.taxAndShippingAddressSynced) return;

			const payload = { ...getters.taxAndShippingAddress };

			if (getters.cartTaxAndShippingAddress) {
				payload.id = getters.cartTaxAndShippingAddress.id;
				payload.address_id = getters.cartTaxAndShippingAddress.id;
			}

			try {
				const cart = await (getters.isDigitalProduct ? updateCartBillingAddress(payload) : updateCartShippingAddress(payload));
				commit('setProductLimits', []);
				if (cart) {
					commit('setCart', cart);
				}
			} catch (error) {
				const productErrors = error?.response?.data?.errors?.product;
				if (productErrors) {
					commit('setProductLimits', productErrors);
				} else {
					applyPayCartHttpErrors(commit, error);
				}
				window.scrollTo({
					top: 0,
					behavior: 'smooth'
				});
			}
		}, 500),

	async checkEmail({
		state,
		commit
	}) {
		commit('setEmailExistsError', await checkEmailExists(state.email));
	},

	async checkBillingPostalCode({
		state,
		commit
	}) {
		commit(
			'setBillingPostalCodeMismatchError',
			!(await validatePostalCode(state.billingAddress))
		);
	},

	async checkShippingPostalCode({
		state,
		commit
	}) {
		commit(
			'setShippingPostalCodeMismatchError',
			!(await validatePostalCode(state.shippingAddress))
		);
	},

	async submitOrder({
		state,
		getters,
		commit,
		dispatch
	}) {
		commit('setSubmittingOrder', true);

		if (state.email) {
			await dispatch('checkEmail');
		}

		if (!getters.hasBillingAddressErrors) {
			await dispatch('checkBillingPostalCode');
		}

		if (
			state.shippingDifferentFromBilling &&
			!getters.hasShippingAddressErrors
		) {
			await dispatch('checkShippingPostalCode');
		}

		if (getters.formHasErrors) {
			commit('setSubmittingOrder', false);
			commit('setShowFormErrors', true);
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
			return;
		}

		dispatch('completeOrder');
	},

	async payCart({
		state,
		getters,
		commit
	}, { payment }) {
		const user = {
			first_name: state.billingAddress.first_name,
			last_name: state.billingAddress.last_name,
			sms_opt_in: false,
		};

		if (!state.user) {
			user.email = state.email;
		} else {
			user.email = state.user.email;
			user.sms_opt_in = state.user.sms_opt_in;
		}

		const billingAddress = { ...state.billingAddress };

		if (getters.cartBillingAddress) {
			billingAddress.id = getters.cartBillingAddress.id;
			billingAddress.address_id = getters.cartBillingAddress.id;
		}

		const payPayload = {
			payment,
			user,
			billing_address: billingAddress,
		};

		if (getters.isPhysicalProduct || getters.billingAddressIsUsaOrCanada) {
			const shippingAddress = { ...getters.addressToShip };

			if (getters.cartShippingAddress) {
				shippingAddress.id = getters.cartShippingAddress.id;
				shippingAddress.address_id = getters.cartShippingAddress.id;
			}

			payPayload.shipping_address = shippingAddress;
		}

		try {
			const syncedCart = await syncCartAddressesForPayment(payPayload);
			if (syncedCart) {
				commit('setCart', syncedCart);
			}
			commit('setProductLimits', []);

			const response = await api.payCart(payPayload);

			if (response.type && response.type === 'error') {
				if (response.error_code === 'uses_exceeded') {
					response.message += '<br>Return to ' +
						'<a href="/cart" style="color:blue;text-decoration:underline">Cart</a> ' +
						'and remove the coupon to continue'
				}
				handleErrorMessage(commit, [response.message]);
				commit('setSubmittingOrder', false);
				await restorePaymentFormIfAvailable(state);
				return;
			}

			try {
				const result = await finishPayCartResponse(response, {
					gateway: payment.gateway,
					handleAdyenThreeDsAction: state.handleAdyenThreeDsAction,
					onBeforeThreeDs: () => {
						commit('setSubmittingOrder', false);
						commit('setAwaitingThreeDs', true);
					},
					redirectToNextUrl: (url) => {
						window.location.href = url;
					},
				});

				if (!result.handled) {
					commit('setSubmittingOrder', false);
					handleErrorMessage(commit, ['There was an issue completing your payment.']);
					await restorePaymentFormIfAvailable(state);
				}
			} catch (threeDsErr) {
				commit('setAwaitingThreeDs', false);
				commit('setSubmittingOrder', false);
				handleErrorMessage(commit, [
					threeDsErr?.message || 'We could not start card verification. Please try again.',
				]);
				await restorePaymentFormIfAvailable(state);
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			}
		} catch (error) {
			const productErrors = error?.response?.data?.errors?.product;
			if (productErrors) {
				commit('setProductLimits', productErrors);
				commit('setErrors', []);
			} else {
				applyPayCartHttpErrors(commit, error);
			}
			commit('setSubmittingOrder', false);
			const restoreResult = await restorePaymentFormIfAvailable(state);
			if (restoreResult?.success === false) {
				commit('setErrors', [...state.errors, restoreResult.message]);
			}
			throw error;
		}
	},

	async completeOrder({
		state,
		commit,
		dispatch
	}) {
		try {
			const {
				token,
				gateway,
				details
			} = await state.getToken();

			await dispatch('payCart', {
				payment: {
					token,
					gateway,
					details,
				},
			});
		} catch (error) {
			commit('setSubmittingOrder', false);
		}
	},

	async paypalCompleteOrder({
		commit,
		dispatch
	}, payload) {
		commit('setProcessingPaypal', true);

		try {
			await dispatch('payCart', {
				payment: {
					token: payload.nonce,
					gateway: 'Braintree',
					paypal: {
						email: payload.details.email,
					},
				},
			});
		} catch (error) {
			commit('setProcessingPaypal', false);
		}
	},

	completeAdyenThreeDs({ commit }, { response }) {
		commit('setAwaitingThreeDs', false);

		const nextUrl = response?.meta?.next_url;
		if (nextUrl) {
			window.location.href = nextUrl;
			return;
		}

		handleErrorMessage(commit, ['There was an issue completing your payment.']);
	},

	async failAdyenThreeDs({ state, commit }, { message }) {
		commit('setAwaitingThreeDs', false);
		commit('setSubmittingOrder', false);
		handleErrorMessage(commit, [
			message || 'We could not verify your card. Please try again.',
		]);

		const restoreResult = await restorePaymentFormIfAvailable(state);
		if (restoreResult?.success === false) {
			commit('setErrors', [...state.errors, restoreResult.message]);
		}
	},
};

function handleErrorMessage(commit, message) {
	commit(
		'setErrors',
		message
	);
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	});
}
